import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, AlertCircle, Clock, User } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { PriorityIndicator } from "@/components/compliance/PriorityIndicator";
import type { ComplianceTask } from "@/hooks/use-compliance-tasks";
import { formatFramework } from "@/hooks/use-compliance-programs";

interface KanbanCardProps {
  task: ComplianceTask;
  onClick: () => void;
  departmentColor?: string;
  departmentName?: string;
  isOverdue?: boolean;
  daysUntilDue?: number | null;
}

function getInitials(name: string) {
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}

export function KanbanCard({ task, onClick, departmentColor = "#6366f1", departmentName = "General", isOverdue = false, daysUntilDue }: KanbanCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({ id: task.id });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform) }}
      className={`bg-card border rounded-lg p-3 shadow-sm cursor-pointer select-none transition-shadow ${
        isDragging ? "opacity-50 shadow-md" : "hover:shadow-md"
      }`}
      onClick={onClick}
    >
      <div className="flex items-start gap-2">
        <div
          {...attributes}
          {...listeners}
          className="mt-0.5 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-medium leading-snug line-clamp-2">{task.title}</p>
            <PriorityIndicator priority={task.priority} />
          </div>

          {task.description && (
            <p className="text-xs text-muted-foreground line-clamp-1">{task.description}</p>
          )}

          {(task.program || task.program_phase || task.control) && (
            <div className="flex flex-wrap gap-1">
              {task.program && <Badge variant="secondary" className="h-5 text-xs">{formatFramework(task.program.framework)}</Badge>}
              {task.program_phase && <Badge variant="outline" className="h-5 text-xs capitalize">{task.program_phase.replace("_", " ")}</Badge>}
              {task.control && <Badge variant="outline" className="h-5 text-xs">{task.control.control_id}</Badge>}
            </div>
          )}

          <div className="flex items-center justify-between flex-wrap gap-1">
            <Badge
              variant="outline"
              className="text-xs h-5"
              style={{
                backgroundColor: departmentColor + "15",
                borderColor: departmentColor,
              }}
            >
              {departmentName}
            </Badge>

            {task.due_date && (
              <span className={`text-xs flex items-center gap-0.5 ${isOverdue ? "text-destructive" : daysUntilDue !== null && daysUntilDue !== undefined && daysUntilDue <= 7 ? "text-orange-600" : "text-muted-foreground"}`}>
                {isOverdue ? (
                  <><AlertCircle className="h-3 w-3" />Overdue</>
                ) : daysUntilDue !== null && daysUntilDue !== undefined && daysUntilDue <= 7 ? (
                  <><Clock className="h-3 w-3" />{daysUntilDue}d</>
                ) : (
                  new Date(task.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })
                )}
              </span>
            )}
          </div>

          {task.primary_assignee && (
            <div className="flex items-center gap-1.5">
              <Avatar className="h-5 w-5">
                <AvatarFallback className="text-[10px]">
                  {getInitials(task.primary_assignee.name)}
                </AvatarFallback>
              </Avatar>
              <span className="text-xs text-muted-foreground truncate">{task.primary_assignee.name}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
